import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { Project, FilterOptions, DashboardStats, ProjectStatus } from '../types';

interface ProjectState {
  projects: Project[];
  archivedProjects: Project[];
  currentProject: Project | null;
  lastViewedProject: Project | null;
  isLoading: boolean;
  stats: DashboardStats | null;
  fetchProjects: (filters?: FilterOptions) => Promise<void>;
  fetchArchivedProjects: () => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  createProject: (project: Partial<Project>) => Promise<Project>;
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  archiveProject: (id: string) => Promise<void>;
  unarchiveProject: (id: string) => Promise<void>;
  fetchStats: () => Promise<void>;
  updateProjectStatus: (id: string, status: ProjectStatus, note?: string) => Promise<any>;
  fetchProjectTimeline: (id: string) => Promise<any>;
  setLastViewedProject: (project: Project) => void;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  archivedProjects: [],
  currentProject: null,
  lastViewedProject: null,
  isLoading: false,
  stats: null,

  fetchProjects: async (filters?: FilterOptions) => {
    if (!isSupabaseConfigured()) {
      set({ projects: [], isLoading: false, stats: {
        totalProjects: 0,
        activeProjects: 0,
        completedProjects: 0,
        pendingDocuments: 0,
        upcomingAppointments: 0,
        fundingTotal: 0
      }});
      return;
    }
    
    set({ isLoading: true });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ projects: [], isLoading: false });
        return;
      }

      let query = supabase
        .from('projects')
        .select(`
          *,
          owner:users!projects_owner_id_fkey(*),
          assigned_advisor:users!projects_assigned_advisor_id_fkey(*),
          steps(*),
          documents(*)
        `)
        .eq('archived', false)
        .order('created_at', { ascending: false });

      // Filtrer selon le profil utilisateur
      const { data: userProfile } = await supabase
        .from('users')
        .select('profile_type')
        .eq('id', user.id)
        .maybeSingle();

      if (userProfile?.profile_type === 'PORTEUR') {
        // Les porteurs ne voient que leurs projets
        query = query.eq('owner_id', user.id);
      } else if (userProfile?.profile_type === 'ACCOMPAGNATEUR') {
        // Les accompagnateurs voient leurs projets assignés + leurs propres projets
        query = query.or(`owner_id.eq.${user.id},assigned_advisor_id.eq.${user.id}`);
      }
      // COORDINATEUR, FINANCEUR, ADMIN voient tous les projets

      if (filters?.status?.length) {
        query = query.in('status', filters.status);
      }
      if (filters?.domain?.length) {
        query = query.in('domain', filters.domain);
      }
      if (filters?.assignedAdvisor?.length) {
        query = query.in('assigned_advisor_id', filters.assignedAdvisor);
      }

      const { data, error } = await query;
      if (error) throw error;

      console.log(`📊 ${data?.length || 0} projets chargés pour ${userProfile?.profile_type} ${user.email}`);
      set({ projects: data || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching projects:', error);
      set({ isLoading: false });
    }
  },

  fetchProject: async (id: string) => {
    if (!isSupabaseConfigured()) {
      set({ currentProject: null, isLoading: false });
      return;
    }
    
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          owner:users!projects_owner_id_fkey(*),
          assigned_advisor:users!projects_assigned_advisor_id_fkey(*),
          steps(*),
          documents(*),
          appointments(*)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      set({ 
        currentProject: data, 
        lastViewedProject: data,
        isLoading: false 
      });
      
      // Save to localStorage for persistence
      if (data) {
        localStorage.setItem('lastViewedProject', JSON.stringify(data));
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      set({ isLoading: false });
    }
  },

  createProject: async (project: Partial<Project>) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
    }
    
    const { data, error } = await supabase
      .from('projects')
      .insert([project])
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!data) throw new Error('Failed to create project');
    
    const { projects } = get();
    set({ projects: [data, ...projects] });
    return data;
  },

  updateProject: async (id: string, updates: Partial<Project>) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
    }
    
    const { error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', id);

    if (error) throw error;

    const { projects, currentProject } = get();
    const updatedProjects = projects.map(p => 
      p.id === id ? { ...p, ...updates } : p
    );
    
    set({
      projects: updatedProjects,
      currentProject: currentProject?.id === id 
        ? { ...currentProject, ...updates } 
        : currentProject,
    });
  },

  deleteProject: async (id: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
    }
    
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) throw error;

    const { projects } = get();
    set({ projects: projects.filter(p => p.id !== id) });
  },

  fetchStats: async () => {
    if (!isSupabaseConfigured()) {
      set({ 
        stats: {
          totalProjects: 0,
          activeProjects: 0,
          completedProjects: 0,
          pendingDocuments: 0,
          upcomingAppointments: 0,
          fundingTotal: 0
        }
      });
      return;
    }
    
    try {
      // Fetch real stats from database
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Récupérer le profil utilisateur
      const { data: userProfile } = await supabase
        .from('users')
        .select('profile_type')
        .eq('id', user.id)
        .maybeSingle();

      // Construire la requête selon le profil
      let projectsQuery = supabase
        .from('projects')
        .select('id, status, funding_requested, funding_amount');

      if (userProfile?.profile_type === 'PORTEUR') {
        projectsQuery = projectsQuery.eq('owner_id', user.id);
      } else if (userProfile?.profile_type === 'ACCOMPAGNATEUR') {
        projectsQuery = projectsQuery.or(`owner_id.eq.${user.id},assigned_advisor_id.eq.${user.id}`);
      }
      // COORDINATEUR, FINANCEUR, ADMIN voient tous les projets

      const { data: projectsData, error: projectsError } = await projectsQuery;

      if (projectsError) throw projectsError;

      const projects = projectsData || [];
      const totalProjects = projects.length;
      const activeProjects = projects.filter(p => 
        ['EN_COURS', 'VALIDE', 'DEBLOCAGE_FONDS'].includes(p.status)
      ).length;
      const completedProjects = projects.filter(p => p.status === 'TERMINE').length;

      // Get documents count
      const projectIds = projects.map(p => p.id);
      let documentsData = [];
      
      if (projectIds.length > 0) {
        const { data, error: documentsError } = await supabase
          .from('documents')
          .select('is_validated, project_id')
          .in('project_id', projectIds);
        
        if (documentsError) throw documentsError;
        documentsData = data || [];
      }

      const pendingDocuments = documentsData?.filter(d => 
        d.is_validated === null || d.is_validated === false
      ).length || 0;

      // Get upcoming appointments
      let appointmentsData = [];
      
      let appointmentsQuery = supabase
        .from('appointments')
        .select('id')
        .gte('start_date', new Date().toISOString());

      if (projectIds.length > 0) {
        appointmentsQuery = appointmentsQuery.or(`created_by.eq.${user.id},project_id.in.(${projectIds.join(',')})`);
      } else {
        appointmentsQuery = appointmentsQuery.eq('created_by', user.id);
      }

      const { data: appointmentsResult, error: appointmentsError } = await appointmentsQuery;
      if (appointmentsError) throw appointmentsError;
      appointmentsData = appointmentsResult || [];

      const upcomingAppointments = appointmentsData?.length || 0;

      // Calculate funding totals
      const fundingTotal = projects.reduce((sum, p) => sum + (p.funding_amount || 0), 0);

      const stats = {
        totalProjects,
        activeProjects,
        completedProjects,
        pendingDocuments,
        upcomingAppointments,
        fundingTotal
      };

      console.log(`📈 Stats calculées pour ${userProfile?.profile_type}:`, stats);
      set({ stats });
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default stats on error
      set({ 
        stats: {
          totalProjects: 0,
          activeProjects: 0,
          completedProjects: 0,
          pendingDocuments: 0,
          upcomingAppointments: 0,
          fundingTotal: 0
        }
      });
    }
  },

  updateProjectStatus: async (id: string, status: ProjectStatus, note?: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré. Veuillez configurer la connexion.');
    }
    
    try {
      // For now, just update the project status directly
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('projects')
        .update({ 
          status,
          current_status: status,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      // Create a stage entry
      const { error: stageError } = await supabase
        .from('project_stages')
        .insert({
          project_id: id,
          status,
          set_by_id: user?.id,
          note
        });

      if (stageError) console.warn('Could not create stage entry:', stageError);

      // Update local state
      const { projects, currentProject } = get();
      const updatedProjects = projects.map(p => 
        p.id === id ? { ...p, current_status: status, status } : p
      );
      
      set({
        projects: updatedProjects,
        currentProject: currentProject?.id === id 
          ? { ...currentProject, current_status: status, status } 
          : currentProject,
      });

      return { success: true };
    } catch (error: any) {
      console.error('Error updating project status:', error);
      throw error;
    }
  },

  fetchProjectTimeline: async (id: string) => {
    if (!isSupabaseConfigured()) {
      return {
        project_id: id,
        current_status: 'A_PREPARER',
        stages: []
      };
    }
    
    try {
      // Get project current status
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('current_status, status')
        .eq('id', id)
        .maybeSingle();

      if (projectError) throw projectError;
      if (!project) {
        return {
          project_id: id,
          current_status: 'A_PREPARER',
          stages: []
        };
      }

      // Get project stages with user names
      const { data: stages, error: stagesError } = await supabase
        .from('project_stages')
        .select(`
          *,
          set_by:users!project_stages_set_by_id_fkey(full_name)
        `)
        .eq('project_id', id)
        .order('reached_at', { ascending: false });

      if (stagesError) throw stagesError;

      // Transform stages to include set_by_name
      const transformedStages = (stages || []).map(stage => ({
        ...stage,
        set_by_name: stage.set_by?.full_name || 'Système'
      }));

      return {
        project_id: id,
        current_status: project.current_status || project.status,
        stages: transformedStages
      };
    } catch (error: any) {
      console.error('Error fetching project timeline:', error);
      return {
        project_id: id,
        current_status: 'A_PREPARER',
        stages: []
      };
    }
  },

  fetchArchivedProjects: async () => {
    if (!isSupabaseConfigured()) {
      set({ archivedProjects: [], isLoading: false });
      return;
    }

    set({ isLoading: true });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ archivedProjects: [], isLoading: false });
        return;
      }

      const { data: userProfile } = await supabase
        .from('users')
        .select('profile_type')
        .eq('id', user.id)
        .maybeSingle();

      if (!['ACCOMPAGNATEUR', 'COORDINATEUR', 'ADMIN'].includes(userProfile?.profile_type || '')) {
        set({ archivedProjects: [], isLoading: false });
        return;
      }

      let query = supabase
        .from('projects')
        .select(`
          *,
          owner:users!projects_owner_id_fkey(*),
          assigned_advisor:users!projects_assigned_advisor_id_fkey(*)
        `)
        .eq('archived', true)
        .order('archived_at', { ascending: false });

      if (userProfile?.profile_type === 'ACCOMPAGNATEUR') {
        query = query.or(`owner_id.eq.${user.id},assigned_advisor_id.eq.${user.id}`);
      }

      const { data, error } = await query;
      if (error) throw error;

      set({ archivedProjects: data || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching archived projects:', error);
      set({ isLoading: false });
    }
  },

  archiveProject: async (id: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré.');
    }

    try {
      const { error } = await supabase.rpc('archive_project', { project_id: id });
      if (error) throw error;

      const { projects } = get();
      set({ projects: projects.filter(p => p.id !== id) });

      await get().fetchArchivedProjects();
    } catch (error: any) {
      console.error('Error archiving project:', error);
      throw error;
    }
  },

  unarchiveProject: async (id: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré.');
    }

    try {
      const { error } = await supabase.rpc('unarchive_project', { project_id: id });
      if (error) throw error;

      const { archivedProjects } = get();
      set({ archivedProjects: archivedProjects.filter(p => p.id !== id) });

      await get().fetchProjects();
    } catch (error: any) {
      console.error('Error unarchiving project:', error);
      throw error;
    }
  },

  setLastViewedProject: (project: Project) => {
    set({ lastViewedProject: project });
    localStorage.setItem('lastViewedProject', JSON.stringify(project));
  },
}));